import { useLocation } from "wouter";
import { Check, Circle, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  WORKFLOW_STAGES,
  computeWorkflowStage,
  getStageIndex,
  type ApprovalStatusLight,
  type WorkflowStage,
} from "@/lib/workflowStages";
import type { ContractLifecycleState } from "@/components/LifecycleStateBadge";

interface WorkflowStatusBarProps {
  contractId: number;
  lifecycleState: ContractLifecycleState;
  approval: ApprovalStatusLight | null;
  /**
   * When true, the current stage renders in an amber/warning tone — used by
   * the parent when there are blocking issues (e.g. an approver requested
   * changes or compliance is failing) so the user can spot the problem.
   */
  blocked?: boolean;
  /**
   * Detail page URL prefix used when a stage chip is clicked. Defaults to
   * `/contracts` so existing callers keep working; the term-sheet detail
   * page passes `/term-sheets` so clicks stay inside the TS surface
   * instead of leaking into the contract detail UI.
   */
  basePath?: string;
  documentKind?: string | null;
  termSheetStatus?: string | null;
}

/**
 * Horizontal stepper that maps the lifecycle + approval state onto six
 * high-level workflow stages: Draft → Internal Review → Ready to Send →
 * Negotiation → Signature → Executed.
 *
 * Each step is clickable and scrolls/jumps to the relevant tab so the user
 * can drill into the underlying status.
 */
export function WorkflowStatusBar({
  contractId,
  lifecycleState,
  approval,
  blocked = false,
  basePath = "/contracts",
  documentKind = null,
  termSheetStatus = null,
}: WorkflowStatusBarProps) {
  const [, setLocation] = useLocation();
  const isTermSheet = documentKind === "term_sheet";
  const currentStage: WorkflowStage = computeWorkflowStage(lifecycleState, approval, {
    documentKind,
    termSheetStatus,
  });
  const currentIdx = getStageIndex(currentStage);

  const goToTab = (tabHash: string) => {
    setLocation(`${basePath}/${contractId}#${tabHash}`);
    // Wouter doesn't trigger hashchange on its own when only the hash changes
    // because the path stayed the same — fire it manually so the detail page
    // picks up the new tab.
    requestAnimationFrame(() => {
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    });
  };

  const stageLabel = (stage: (typeof WORKFLOW_STAGES)[number]) => {
    if (!isTermSheet) return stage.label;
    if (stage.id === "negotiation") return "External Send";
    if (stage.id === "signature") return "Agree";
    if (stage.id === "executed") return "Accepted";
    if (stage.id === "ready_to_send") return "Ready to Send";
    return stage.label;
  };

  return (
    <div
      className="rounded-xl border bg-card px-4 py-3 sm:px-5 sm:py-4 shadow-sm"
      data-testid="workflow-status-bar"
      role="navigation"
      aria-label="Contract workflow stages"
    >
      <ol className="flex items-stretch gap-1 sm:gap-2 overflow-x-auto">
        {WORKFLOW_STAGES.map((stage, idx) => {
          const isCompleted = idx < currentIdx;
          const isCurrent = idx === currentIdx;
          const isFuture = idx > currentIdx;
          const isCurrentBlocked = isCurrent && blocked;

          return (
            <li key={stage.id} className="flex-1 min-w-[110px]">
              <button
                type="button"
                onClick={() => goToTab(stage.tabHash)}
                aria-current={isCurrent ? "step" : undefined}
                data-testid={`workflow-stage-${stage.id}`}
                className={cn(
                  "group w-full text-left rounded-lg px-2.5 py-2 transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  "hover:bg-muted/50",
                )}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold border",
                      isCompleted && "bg-green-600 text-white border-green-600",
                      isCurrent && !isCurrentBlocked && "bg-primary text-primary-foreground border-primary",
                      isCurrentBlocked && "bg-amber-500 text-white border-amber-500",
                      isFuture && "bg-muted text-muted-foreground border-border",
                    )}
                    aria-hidden="true"
                  >
                    {isCompleted ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : isCurrentBlocked ? (
                      <AlertTriangle className="h-3.5 w-3.5" />
                    ) : isCurrent ? (
                      <Circle className="h-2 w-2 fill-current" />
                    ) : (
                      <span>{idx + 1}</span>
                    )}
                  </span>
                  <span
                    className={cn(
                      "text-xs sm:text-sm font-medium truncate",
                      isCompleted && "text-foreground",
                      isCurrent && !isCurrentBlocked && "text-foreground",
                      isCurrentBlocked && "text-amber-700",
                      isFuture && "text-muted-foreground",
                    )}
                  >
                    {stageLabel(stage)}
                  </span>
                </div>
                <div
                  className={cn(
                    "mt-2 h-1 rounded-full",
                    isCompleted && "bg-green-600",
                    isCurrent && !isCurrentBlocked && "bg-primary",
                    isCurrentBlocked && "bg-amber-500",
                    isFuture && "bg-muted",
                  )}
                />
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
